/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button, Checkbox, Grid } from '@mui/material';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import {
  DragDropContext,
  Droppable,
  Draggable,
} from '@atlaskit/pragmatic-drag-and-drop-react-beautiful-dnd-migration';
import IngredientListItem from './ingredient_list_item';
import IngredientLineContainer from './ingredient_line_container';
import {
  ingredientNutritionItem,
  mealWithArraySectionMoved,
} from '../../services/meals';
import { arraySectionFullTextIsHeading } from '../../services/ingredients';
import { useTracker } from '../../operations/tracking_operations';
import { useUpdateMealMutation } from '../../app/smorgAppSyncGraphQLApi';
import { useGetUserProfileQuery } from '../../app/smorgAppSyncGraphQLApiCustomHooks';

const ingredientIsUnlinked = (ing) => {
  return (
    ing.structuredIngredient &&
    !ing.structuredIngredient.linkedIngredient?.status
  );
};

export const IngredientListMobile = ({
  meal,
  readOnly,
  savingInProgress,
  ingredientSuggestions,
  ingredientSuggestionsNetworkState,
  ingredientNutrition,
  onRemoveItem,
  onAddItem,
  currentlyEditingInline,
  onInlineEditFocus,
  onInlineEditChange,
  onInlineEditBlur,
  editMode,
  onIngredientCheckChange,
  onUpdateIngredientStructuredData,
}) => {
  const recipe = meal && meal.recipes[0];
  const tracker = useTracker();
  const { data: userProfile } = useGetUserProfileQuery();

  const [updateMeal] = useUpdateMealMutation();

  const handleDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }
    const updatedMeal = mealWithArraySectionMoved(
      meal,
      'ingredients',
      result.source.index,
      result.destination.index,
    );
    updateMeal(updatedMeal);
    tracker(userProfile, ['Edit meal'], ['numMealsEdited']);
  };

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="ingredientsDroppable">
          {(droppableProvided) => (
            <div
              {...droppableProvided.droppableProps}
              ref={droppableProvided.innerRef}>
              {recipe.ingredients.map((ing, index) => {
                const ingID = ing.id || ing.fullText;
                const emptyIngredientLabel = !editMode && !ing?.fullText;
                const ingNutritionItem = ingredientNutritionItem(
                  ingredientNutrition,
                  ing.id,
                );

                console.log(ing.fullText);
                return (
                  <Draggable
                    key={ingID}
                    draggableId={ingID}
                    isDragDisabled={readOnly}
                    index={index}>
                    {(provided) => (
                      <Grid
                        container
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        alignItems="center"
                        className={classNames({
                          unlinked: ingredientIsUnlinked(ing),
                        })}>
                        <Grid item xs={12}>
                          <Grid container>
                            <Grid
                              item
                              xs={1}
                              flexShrink={0}
                              flexGrow={0}
                              style={{ display: 'flex', minHeight: 38 }}>
                              <div
                                {...provided.dragHandleProps}
                                style={{
                                  margin: 'auto 0',
                                  paddingTop: 4,
                                  paddingRight: 8,
                                  paddingLeft: 0,
                                  display: editMode ? 'block' : 'none',
                                }}>
                                {!readOnly && <DragHandleIcon />}
                              </div>
                              {!editMode &&
                                onIngredientCheckChange &&
                                !arraySectionFullTextIsHeading(ing.fullText) &&
                                !emptyIngredientLabel && (
                                  <Checkbox
                                    checked={!!ing.checkedInIngredientsList}
                                    size="small"
                                    style={{ paddingRight: 4, paddingLeft: 4 }}
                                    onChange={(ev) => {
                                      onIngredientCheckChange(
                                        ing.id,
                                        ev.target.checked,
                                      );
                                    }}
                                  />
                                )}
                            </Grid>
                            <Grid
                              item
                              style={{ display: 'flex' }}
                              xs={editMode ? 9 : 11}>
                              <IngredientLineContainer
                                inputId={`${currentlyEditingInline?.section}-${currentlyEditingInline?.itemId}`}
                                ingredient={ing}
                                ingredientNutrition={
                                  ingNutritionItem?.nutrition
                                }
                                suggestions={ingredientSuggestions}
                                suggestionsLoading={
                                  ingredientSuggestionsNetworkState?.loading
                                }
                                disabled={savingInProgress}
                                onChange={(value, reason) =>
                                  onInlineEditChange(
                                    'ingredients',
                                    ing.id,
                                    value,
                                    reason,
                                  )
                                }
                                onUpdateIngredientStructuredData={(...args) => {
                                  onInlineEditBlur(
                                    'ingredients',
                                    ing.id,
                                    'smorgCancel',
                                  );
                                  onUpdateIngredientStructuredData(...args);
                                }}
                                onDismiss={(reason) =>
                                  onInlineEditBlur(
                                    'ingredients',
                                    ing.id,
                                    reason,
                                  )
                                }
                                isEditing={
                                  editMode &&
                                  currentlyEditingInline?.section ===
                                    'ingredients' &&
                                  currentlyEditingInline?.itemId === ing.id
                                }
                                clickToEditEnabled={editMode}
                                onClickToEdit={() =>
                                  onInlineEditFocus('ingredients', ing.id)
                                }
                              />
                            </Grid>
                            {editMode && (
                              <Grid
                                container
                                style={{ justifyContent: 'flex-end' }}
                                wrap="nowrap"
                                xs={2}>
                                {!readOnly && (
                                  <IngredientListItem
                                    editMode={editMode}
                                    disabled={savingInProgress}
                                    onRemoveItem={onRemoveItem}
                                    itemId={ing.id}
                                  />
                                )}
                              </Grid>
                            )}
                          </Grid>
                        </Grid>
                      </Grid>
                    )}
                  </Draggable>
                );
              })}
              {droppableProvided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      {!readOnly && (
        <Grid container>
          <Grid item xs={9}>
            <Grid container>
              <Grid item xs={1}>
                &nbsp;
              </Grid>
              <Grid item xs={9}>
                <Button
                  onClick={onAddItem}
                  variant="contained"
                  size="medium"
                  color="secondary"
                  disableElevation
                  style={{ justifyContent: 'flex-start' }}>
                  Add an ingredient
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </>
  );
};

IngredientListMobile.propTypes = {
  meal: PropTypes.shape({
    recipes: PropTypes.arrayOf(
      PropTypes.shape({
        ingredients: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
        servings: PropTypes.number,
      }),
    ),
  }).isRequired,
  readOnly: PropTypes.bool.isRequired,
  savingInProgress: PropTypes.bool.isRequired,
  ingredientSuggestions: PropTypes.arrayOf(
    PropTypes.shape({
      suggestionText: PropTypes.string.isRequired,
    }),
  ).isRequired,
  ingredientSuggestionsNetworkState: PropTypes.shape({
    loading: PropTypes.bool,
  }).isRequired,
  currentlyEditingInline: PropTypes.shape({
    section: PropTypes.string.isRequired,
    itemId: PropTypes.string,
  }).isRequired,
  ingredientNutrition: PropTypes.shape({}).isRequired,
  onRemoveItem: PropTypes.func.isRequired,
  onAddItem: PropTypes.func.isRequired,
  onInlineEditFocus: PropTypes.func.isRequired,
  onInlineEditChange: PropTypes.func.isRequired,
  onInlineEditBlur: PropTypes.func.isRequired,
  editMode: PropTypes.bool.isRequired,
  onIngredientCheckChange: PropTypes.func.isRequired,
  onUpdateIngredientStructuredData: PropTypes.func.isRequired,
};

export default IngredientListMobile;
